import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useRef
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import message from "components/CustomMessage";
import { FieldTypes, parseSelectOptions,generateFormFields } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import { Form } from "antd";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

const CycodeMappingForm = forwardRef(function CycodeMappingForm({disableProjectFields = false, ...props}, ref) {
  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { repositoryIds, repositoryNames } = props.record;
      const repoIdArray = repositoryIds?.split(",");
      const repoNameArray = repositoryNames?.split(",");
      return repoIdArray?.map((id, index) => ({
        id,
        name: repoNameArray[index],
      }));
    }
    return [];
  });
  const IntRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [repoList, setRepoList] = useState();
  const [form] = Form.useForm();
  const featureFlags = useFeatureFlag();

  const fetchProject = useCallback(
    (params) => {
      let { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          type: "project",
          ...params,
        });
      promise
        .then((response) => {
          const { projects } = response;
          setProjects(projects);
        })
        .catch((err) => {
          message.error("Problem fetching projects");
        })
        .finally(() => setLoading(false));
      promise = promise.then((res) => res.projects);
      return { promise, cancel };
    },
    [props.loginConfigId, props.toolName]
  );

  const fetchRepos = useCallback((project) => {
    const params = {
      login_id: props.loginConfigId,
      type: "repository",
      ...(project && project)
    };

    let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
    promise
      .then((response) => {
        const { projects } = response;
        let repositoriesToDisplay = parseSelectOptions(projects);
        setRepoList(repositoriesToDisplay);
      })
      .catch((err) => {
        message.error("Problem fetching repositories");
      })
      .finally(() => setLoading(false));
    promise = promise.then(res => res.projects);
    return { promise, cancel };
  }, [props.loginConfigId, props.toolName]);  


  const getExtraFieldsData = (config) => {
    const { repositoryIds , project,cspm, cspmCheck} = config;    
    const data = repoList.reduce((acc,cv) =>{
      const { label, value } = cv;
      if(repositoryIds?.includes(value)){
        acc.id.push(value);
        acc.name.push(label);
      }
      return acc;
    },{id:[],name:[]})
    const payload = {
      repositoryIds: data.id.join(),
      repositoryNames: data.name.join(),
    };

    if(project){
      let projectObj = projects.find(p => p.id === project)
      const projectName = projectObj.name;
      payload.projectId = project;
      payload.projectName = projectName;
    }

    if (cspm && cspmCheck) {
      payload.type = "CSPM";
      payload.accountId = "all";
      payload.accountName = "all";
    }

    if (!payload.repositoryIds && !payload.projectId && !payload.type) {
      let errorMsg;
      if(featureFlags.CYCODE_PULL_CLOUD_ACCOUNT_VIOLATIONS){
        errorMsg = "Please select at least one Repository, Project, or CSPM option.";
      }else{
        errorMsg = "Please select at least one Repository or Project";
      }
      message.error(errorMsg);
      throw new Error(errorMsg);
    }
    return {
      ...payload,
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    };
  };

  const onProjectChange = useCallback(
    (value, field) => {
      // setProjectID(value)
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      let updatedConfiguration = [...configurations];
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        repositoryIds: undefined,
      };
      form.setFieldsValue({ configurations: updatedConfiguration });
    },
    [form]
  );

  const cspmRender = useCallback(
    (listField) => {
      const cspmFields = [
        {
          name: "cspm",
          label: "Fetch all Cloud and Container Security Findings",
          type: FieldTypes.BOOLEAN,
          inline: true,
          disabled: disableProjectFields,
          colSpan: 12,
          rules: [
            {
              validator: (rule, value) => {
                const config =
                  form.getFieldValue("configurations")[listField.name];
                if (
                  (!value && !config.repositoryIds) ||
                  (!config.cspmCheck && !config.repositoryCheck)
                ) {
                  return Promise.reject("Please select either of the fields");
                }
                return Promise.resolve();
              },
            },
          ],
        },
      ];

      const cspmChecked =
        form.getFieldValue("configurations")[listField.name]?.cspmCheck;

      return cspmChecked ? generateFormFields(cspmFields, 10, listField) : null;
    },
    [disableProjectFields, form]
  );

  const extraFields = useMemo(() => [
    {
      name: "project",
      colSpan: "12",
      type: FieldTypes.SELECT,
      label: "Project",
      disabled: disableProjectFields,
      required: false,
      props: {
        autoFocus: !disableProjectFields,
        api: fetchProject,
        onChange: onProjectChange,
        allowClear: true,
      }
    },
    {
      name: "repositoryIds",
      colSpan: "12",
      loading: loading,
      type: FieldTypes.MULTI_SELECT,
      label: "Repository",
      disabled: disableProjectFields,
      required: false,
      props: {
        setExtraProps: (field, listField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const project = allConfigs[listField.name]?.project;
          if (!IntRef.current[project]) {
            IntRef.current[project] = { project };
          }
          return {
            api: fetchRepos,
            apiParams: IntRef.current[project],
            resetOptionsProp: project,
            mode: "multiple",
            allowClear: true,
          }
        }
      }
    },
    ...(featureFlags.CYCODE_PULL_CLOUD_ACCOUNT_VIOLATIONS ? [{
      name: "cspmCheck",
      label: "CSPM",
      type: FieldTypes.BOOLEAN,
      inline: true,
      disabled: disableProjectFields,
    },
    {
      name: (listField) => [listField.name, "cspmList"],
      render: cspmRender,
    }] : []),
  ],
  [loading,cspmRender, disableProjectFields, fetchRepos, fetchProject, form, onProjectChange, featureFlags.CYCODE_PULL_CLOUD_ACCOUNT_VIOLATIONS]
);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    let data = {};
    data["repositoryIds"] = undefined;

    if(config.repositoryIds){
      data["repositoryIds"] = config.repositoryIds?.split(",");
    }
    if(config.projectName){
      data["project"] = config.projectId;
    }

    if(featureFlags.CYCODE_PULL_CLOUD_ACCOUNT_VIOLATIONS){
      data["cspm"] = config.accountId === "all";
      data["cspmCheck"] = config.accountId === "all";
    }

    return {
      ...data,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }) 
    };
  }, [featureFlags.CYCODE_PULL_CLOUD_ACCOUNT_VIOLATIONS]);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default CycodeMappingForm;